"use client"
import { EditorContent } from "@/components/EditorContent"
import { LinkButton } from "@/components/LinkButton"
import { IconButton } from "@/components/ui/IconButton"
import { createAssetUrl } from "@/lib/s3"
import { cn } from "@/lib/tailwind"
import type { ExploreSlide } from "@prisma/client"
import { useKeenSlider } from "keen-slider/react"
import { ArrowLeft, ArrowRight } from "lucide-react"
import * as React from "react"

interface Props {
  exploreSlides: Pick<ExploreSlide, "id" | "cover" | "link" | "linkText" | "title" | "description">[]
}

export function ExploreSlider({ exploreSlides }: Props) {
  const [currentSlide, setCurrentSlide] = React.useState(0)
  const [sliderRef, instanceRef] = useKeenSlider(
    {
      loop: exploreSlides.length > 1,
      drag: exploreSlides.length > 1,
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel)
      },
      slides: { perView: 1, spacing: 15 },
    },
    [
      (slider) => {
        if (exploreSlides.length < 1) return
        let timeout: ReturnType<typeof setTimeout>
        let mouseOver = false
        function clearNextTimeout() {
          clearTimeout(timeout)
        }
        function nextTimeout() {
          clearTimeout(timeout)
          if (mouseOver) return
          timeout = setTimeout(() => {
            slider.next()
          }, 6000)
        }
        slider.on("created", () => {
          slider.container.addEventListener("mouseover", () => {
            mouseOver = true
            clearNextTimeout()
          })
          slider.container.addEventListener("mouseout", () => {
            mouseOver = false
            nextTimeout()
          })
          nextTimeout()
        })
        slider.on("dragStarted", clearNextTimeout)
        slider.on("animationEnded", nextTimeout)
        slider.on("updated", nextTimeout)
      },
    ],
  )

  return (
    <div className="relative space-y-2">
      <div ref={sliderRef} className="keen-slider h-[330px]">
        {exploreSlides.map((exploreSlide, i) => (
          <div
            key={exploreSlide.id}
            className={cn(
              `keen-slider__slide min-w-full number-slide${i + 1} flex w-full flex-col rounded-xl bg-center bg-cover bg-no-repeat py-4 md:py-8 `,
              exploreSlides.length > 1 ? "px-16" : "px-4 md:px-8",
            )}
            style={{ backgroundImage: `url(${createAssetUrl(exploreSlide.cover) || ""})` }}
          >
            <div className="max-w-unset space-y-4 overflow-hidden lg:max-w-[50%]">
              <h2 className="font-bold text-3xl text-gray-900 uppercase">{exploreSlide.title}</h2>

              <EditorContent className="!text-black" content={exploreSlide.description} />

              {exploreSlide.link && (
                <div>
                  <LinkButton
                    href={exploreSlide.link}
                    variant="outline"
                    className="!text-black active:!bg-white hover:!border-black hover:!bg-gray-100 w-min rounded-full bg-white"
                    leftIcon={<ArrowRight size={18} />}
                  >
                    {exploreSlide.linkText || "Check it out"}
                  </LinkButton>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {exploreSlides.length > 1 && (
        <>
          <IconButton
            aria-label="previous slide"
            className="-translate-y-1/2 absolute top-1/2 left-2 transform rounded-full"
            variant="outline"
            onClick={() => instanceRef.current?.prev()}
            icon={<ArrowLeft className="text-black" size={16} />}
          />
          <IconButton
            aria-label="next slide"
            className="-translate-y-1/2 absolute top-1/2 right-2 transform rounded-full"
            variant="outline"
            onClick={() => instanceRef.current?.next()}
            icon={<ArrowRight className="text-black" size={16} />}
          />

          <div className="absolute bottom-4 z-10 flex w-full justify-center">
            <div className="flex space-x-2">
              {[...Array.from({ length: exploreSlides.length })].map((_, index) => (
                <button
                  type="button"
                  className={`h-2.5 w-2.5 rounded-full ${currentSlide === index ? "bg-white/70" : "bg-black/70"}`}
                  key={index}
                  onClick={() => instanceRef.current?.moveToIdx(index)}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
